/* One guided tour per role. Each step points at an element marked data-tour="..." (see SideNav, TopUser and the
   procurement pages). A step with no element is a centred welcome box. Steps whose element is not on screen
   (a menu this user cannot see, a button on another page) are dropped when the tour starts, so keep every tour
   to 4-6 steps and end it with "take-tour". */
import { APP_NAME } from "@/lib/brand";

const at = (name) => `[data-tour="${name}"]`;

const welcome = (title, description) => ({ popover: { title, description } });
const step = (name, title, description, side = "right") => ({ element: at(name), popover: { title, description, side } });

const TAKE_A_TOUR = step("take-tour", "Need this again?", "Click here any time to see this tour again.", "bottom");

export const TOURS = {
  ORG_ADMIN: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at the places you will use most. You can close this at any time."),
    step("nav-projects", "Projects", "Open a project to see its plan, progress, costs and daily reports."),
    step("nav-procurement", "Procurement", "Indents, orders, goods received and vendor bills are all here."),
    step("nav-finance", "Finance", "Accounts, ledgers and period close."),
    step("nav-administration", "Administration", "Add users, set who can see what, and check the audit log."),
    TAKE_A_TOUR,
  ],
  PORTFOLIO_LEAD: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at how to follow all your projects. You can close this at any time."),
    step("nav-workspace", "My Workspace", "Your own list of things waiting for you."),
    step("nav-projects", "Projects", "See every project and open any one for its progress and costs."),
    step("nav-reports", "Reports", "Summary reports across projects."),
    step("top-search", "Find any page", "Type a page name to jump straight to it. Ctrl+K works too.", "bottom"),
    TAKE_A_TOUR,
  ],
  MEMBER: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look around. You can close this at any time."),
    step("nav-workspace", "My Workspace", "Start here to see your work for today."),
    step("nav-projects", "Projects", "Open your project to enter daily progress and see the plan."),
    step("top-bell", "Notifications", "Anything that needs your attention shows up here.", "bottom"),
    step("top-search", "Find any page", "Type a page name to jump straight to it.", "bottom"),
    TAKE_A_TOUR,
  ],
  ACCOUNTS: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at where your accounts work is. You can close this at any time."),
    step("nav-finance", "Finance", "Ledgers, journals and period close."),
    step("nav-procurement", "Procurement", "Vendor bills and payments are under this menu."),
    step("bill-new", "New bill", "Enter a vendor's bill here. Add the vendor's bill number before posting.", "bottom"),
    step("nav-reports", "Reports", "Aging, cost and statement reports."),
    TAKE_A_TOUR,
  ],
  FINANCE_APPROVER: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at the checks you make before money moves. You can close this at any time."),
    step("nav-procurement", "Bills and payments", "Vendor bills wait here for you to verify, and payments wait for you to release."),
    step("nav-finance", "Finance", "Ledgers and the cash and bank position behind each payment."),
    step("top-bell", "Notifications", "Cash requests from sites and bills waiting for you show up here.", "bottom"),
    step("nav-reports", "Reports", "Ageing, cost and statement reports."),
    TAKE_A_TOUR,
  ],
  PROCUREMENT: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at the buying steps. You can close this at any time."),
    step("nav-procurement", "Procurement", "From indent to order to goods received to bill, in one place."),
    step("indent-new", "New indent", "Ask for material for a site here.", "bottom"),
    step("wo-new", "New work order", "Give work to a subcontractor here.", "bottom"),
    step("nav-inventory", "Inventory", "See what is in the store and what has been issued."),
    TAKE_A_TOUR,
  ],
  STORES: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at the store work. You can close this at any time."),
    step("nav-inventory", "Inventory", "Stock, issues to site, returns and transfers."),
    step("nav-procurement", "Goods received", "Record material that arrives against an order."),
    step("grn-new", "New goods receipt", "Enter what was delivered. You cannot receive more than was ordered.", "bottom"),
    step("top-bell", "Notifications", "New orders and approvals show up here.", "bottom"),
    TAKE_A_TOUR,
  ],
  HR: [
    welcome(`Welcome to ${APP_NAME}`, "A quick look at the people work. You can close this at any time."),
    step("nav-hr-and-payroll", "HR & Payroll", "Employees, attendance and monthly payroll."),
    step("nav-workspace", "My Workspace", "Your own list of things waiting for you."),
    step("nav-reports", "Reports", "Manpower and attendance reports."),
    step("top-bell", "Notifications", "Anything that needs your attention shows up here.", "bottom"),
    TAKE_A_TOUR,
  ],
};

/** The tour for a role; an unknown role gets the general one. */
export const tourFor = (role) => TOURS[role] || TOURS.MEMBER;
